
import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Technique } from '@/types/technique';
import { Button } from '@/components/ui/button';
import { Bookmark, BookmarkCheck, Share2, ChevronLeft } from 'lucide-react';
import { StatusLabel } from '@/components/StatusLabel';

interface TechniqueHeaderProps {
  technique: Technique;
  isSaved: boolean;
  onToggleSave: (e: React.MouseEvent) => void;
  onShare: () => void;
}

export const TechniqueHeader: React.FC<TechniqueHeaderProps> = ({
  technique,
  isSaved,
  onToggleSave,
  onShare,
}) => {
  const navigate = useNavigate();

  const handleBack = () => {
    if (window.history.length > 1) {
      navigate(-1);
    } else {
      navigate('/techniques');
    }
  };

  return (
    <div className="flex flex-col gap-4">
      {/* Back button */}
      <Button 
        variant="ghost" 
        size="sm" 
        onClick={handleBack}
        className="self-start gap-2 -ml-2"
      >
        <ChevronLeft className="h-4 w-4" />
        <span className="hidden sm:inline">Back</span>
      </Button>

      {/* Main header content */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-3xl font-display font-bold tracking-tight">{technique.name}</h1>
          <div className="flex flex-wrap gap-2 mt-2">
            {technique.position && (
              <span className="text-sm bg-secondary/50 px-2 py-1 rounded-full">
                {technique.position.name}
              </span>
            )}
            <StatusLabel type={technique.technique_position} />
            <StatusLabel type={technique.technique_type} />
            <StatusLabel type={technique.difficulty} />
          </div>
        </div>
        
        <div className="flex gap-2 self-start">
          <Button 
            variant="outline" 
            size="sm" 
            className="gap-2"
            onClick={onShare}
          >
            <Share2 className="h-4 w-4" />
            Share
          </Button>

          <Button 
            variant={isSaved ? "default" : "outline"} 
            className="gap-2"
            onClick={(e: React.MouseEvent) => onToggleSave(e)}
          >
            {isSaved ? (
              <>
                <BookmarkCheck className="h-4 w-4" />
                Saved to My Techniques
              </>
            ) : (
              <>
                <Bookmark className="h-4 w-4" />
                Save Technique
              </>
            )}
          </Button>
        </div>
      </div>
    </div>
  );
};
